import React, { lazy, Suspense } from 'react';
import cn from 'classnames';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Button from 'components/UI/Button';
import Link from 'components/UI/Link';
import Image from 'components/UI/Image';
import { useStateModal } from 'utils/hooks';
import { useTrans } from 'utils/localisation';
import { withErrorBoundary } from 'components/ErrorBoundary';
import * as styles from './CallToAction.module.scss';

const RequestConsultation = lazy(() => import('components/Modals/RequestConsultation'));

function CallToAction({
  id = 'call-to-action',
  title,
  buttonText,
  buttonLink,
  buttonTarget = '_blank',
  buttonRel = 'noreferrer noindex nofollow',
  additionalLink,
  theme,
  image,
  formName,
  withNda,
  withAgreement,
  withAttach,
  commentPlaceholder,
  commentPlaceholderMessage,
  modalTitle,
  modalAsideData,
  className,
  margin,
  background,
}) {
  const { modalState, showModal, hideModal } = useStateModal();
  const t = useTrans(['common']);
  const defaultFormName = withAgreement ? 'cta-with-agreement' : 'cta-block';
  const preparedButtonText = buttonText || t('contactUs');

  return (
    <Container
      id={id}
      className={cn(styles.container, styles[theme], image && styles.withImg, className)}
      margin={margin}
      background={background}
    >
      <div className={styles.wrapper}>
        <div className={styles.titleRow}>
          <Title tag="p" size="h3">
            {title}
          </Title>
        </div>
        <div className={styles.buttonRow}>
          {buttonLink ? (
            <Button
              tag="a"
              target={buttonTarget}
              href={buttonLink}
              rel={buttonRel}
              className={styles.button}
            >
              {preparedButtonText}
            </Button>
          ) : (
            <Button type="submit" className={styles.button} onClick={showModal}>
              {preparedButtonText}
            </Button>
          )}
          {additionalLink && (
            <Link href={additionalLink} target="_blank" className={styles.link}>
              {t('seeMore')}
            </Link>
          )}
        </div>
      </div>
      {image && <Image src={image} className={styles.picture} classImage={styles.image} />}
      <Suspense fallback={null}>
        <RequestConsultation
          isOpen={modalState.isOpen}
          closeModalCallback={hideModal}
          animation="show"
          title={modalTitle || preparedButtonText}
          commentPlaceholder={commentPlaceholder}
          commentPlaceholderMessage={commentPlaceholderMessage}
          id={defaultFormName}
          formName={formName ?? defaultFormName}
          withAgreement={withAgreement}
          withNda={withNda}
          withAttach={withAttach}
          modalAsideData={modalAsideData}
        />
      </Suspense>
    </Container>
  );
}

CallToAction.displayName = 'CallToAction';

export default withErrorBoundary(CallToAction);
